import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import {
  FormRowLabel,
  FormRowLayout,
  FormRowValue,
} from '@/common/components/FormRow/FormRow.tsx'
import {
  FamilyApplicationDetails,
  Student,
} from '@/queries/family_applications/requests.ts'
import React from 'react'
import { Disclosure, DisclosureButton } from '@headlessui/react'
import { MinusIcon, PlusIcon } from '@/common/icons/arrows.tsx'
import Image from '@/common/components/Image.tsx'
import { useApplicationDocumentLogging } from '@/pages/applicants/applicant_details/hooks/useApplicationDocumentLogging.ts'
import { useQuery } from '@tanstack/react-query'
import { StateQueries } from '@/queries/states/queries.ts'

interface Props {
  application?: FamilyApplicationDetails | null
  selectedStudent?: Student | null
  status?: string | null
}

const AplicantDetails: React.FC<Props> = ({ application, selectedStudent }) => {
  const logView = useApplicationDocumentLogging({
    logType: 'family',
    applicantId: application?.user_id ?? null,
    documentsViewed: application?.family_documents,
    docsViewedType: 'IDENTITY',
  })

  const StateData = useQuery(
    StateQueries.getStateById(application?.global_state_id, {
      staleTime: Infinity,
    })
  )

  return (
    <Card>
      <CardHeader>Applicant Details</CardHeader>
      <CardContent>
        <FormRowLayout>
          <FormRowLabel>Applicant ID</FormRowLabel>
          <FormRowValue>{application?.user_id}</FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Name</FormRowLabel>
          <FormRowValue>{application?.name}</FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Birth Date</FormRowLabel>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Address</FormRowLabel>
          <FormRowValue>
            {StateData?.status === 'pending' ? (
              <div>Loading</div>
            ) : (
              <div className="text-right">
                {application?.address} <br /> {application?.city},{' '}
                {StateData?.data?.name}, {application?.zip_code}
              </div>
            )}
          </FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Address 2</FormRowLabel>
          <FormRowValue>{application?.address2 ?? 'Not added'}</FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>County</FormRowLabel>
          <FormRowValue>{application?.county ?? 'Not added'}</FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Email</FormRowLabel>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Phone</FormRowLabel>
          <FormRowValue>{application?.phone ?? 'Not added'}</FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>SSN</FormRowLabel>
          <FormRowValue>
            {' '}
            {application?.SSN ? '***-**-' + application?.SSN : 'Not added'}
          </FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Contact Preference</FormRowLabel>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Relationship to selected Student</FormRowLabel>
          <FormRowValue>
            {selectedStudent ? selectedStudent?.relationship : 'N/A'}
          </FormRowValue>
        </FormRowLayout>

        <FormRowLayout className="flex-col items-start border-b-0">
          <Disclosure>
            {({ open }) => {
              if (open) {
                logView()
              }
              return (
                <>
                  <div className="flex flex-row w-full items-center justify-between">
                    <FormRowLabel>Identity/Residency Documents</FormRowLabel>
                    <DisclosureButton className="flex items-center">
                      <span className="text-sm text-gray-400">
                        {open ? <MinusIcon /> : <PlusIcon />}
                      </span>
                    </DisclosureButton>
                  </div>
                  {open && (
                    <div className="grid lg:grid-cols-3 gap-4 mt-8">
                      {application?.family_documents
                        ?.filter((document) => document?.type === 'IDENTITY')
                        .map((document) => (
                          <Image
                            key={document?.image_name_front}
                            allowZoom={true}
                            path={document?.image_name_front}
                            width={'100%'}
                            alt={document?.variation}
                          />
                        ))}
                    </div>
                  )}
                </>
              )
            }}
          </Disclosure>
        </FormRowLayout>
      </CardContent>
    </Card>
  )
}
export default AplicantDetails
